import React, { ChangeEvent, memo, useCallback, useState } from "react";
import { IconButton, Table, TableBody, TableContainer, TableHead, TableRow } from "@mui/material";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import TableCellComponent from "components/table/TableCellComponent";
import TableRowComponent from "components/table/TableRowComponent";
import ButtonComponent from "components/button/ButtonComponent";
import Notify from "config/notify";
import { TInputCode } from "../types/wheel-code";

// Constants
const WHEEL_CODE_HEADER = [
  {
    label: "商品コード",
    sx: { width: "231px", textAlign: "center", paddingX: "8px" },
  },
  {
    label: "商品名",
    sx: { width: "385px", textAlign: "center", paddingX: "8px" },
  },
  {
    label: "サイズ",
    sx: { width: "281px", textAlign: "center", paddingX: "8px" },
  },
  {
    label: "荷重指数＋速度記号",
    sx: { width: "203px", textAlign: "center", paddingX: "8px" },
  },
];

// Styles
const styles = {
  inputText: {
    "& input": { textAlign: "center", p: "11px", fontSize: { xs: "16px", xl: "20px" } },
    width: 185,
    height: 50,
    borderRadius: "8px",
  },
  inputTextNoBorder: {
    border: "none",
    height: 50,
    "& input": { textAlign: "center", p: "11px", fontSize: { xs: "16px", xl: "20px" } },
    "& .MuiOutlinedInput-notchedOutline": { border: "none" },
  },
  inputTextLeftAlign: {
    border: "none",
    height: 50,
    "& input": { textAlign: "left", p: "11px", fontSize: { xs: "16px", xl: "20px" } },
    "& .MuiOutlinedInput-notchedOutline": { border: "none" },
  },
  tableCell: {
    textAlign: "center",
    background: "white",
    p: 2,
    borderBottom: "none",
  },
  tableCellBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexDirection: "column",
    gap: "13px",
  },
  tableCellBoxLeft: {
    display: "flex",
    alignItems: "left",
    justifyContent: "space-between",
    flexDirection: "column",
    gap: "13px",
  },
  tableContainer: {
    width: "max-content",
    boxShadow: "none",
  },
  removeButton: {
    color: "var(--black)",
    cursor: "pointer",
  },
  searchButton: {
    width: 86,
  },
  sectionTitle: {
    fontSize: { xs: 16, md: 18 },
    fontWeight: "medium",
  },
} as const;

// Types
interface BaseItemData {
  productCode: string;
  productName: string;
  size: string;
  loadIndex: string;
  isSearchMode: boolean;
  hasError?: boolean;
  rimDiam?: string | null;
}

interface WheelData extends BaseItemData {}

interface TireData extends BaseItemData {}

interface SearchRowData {
  id: string;
  wheels: WheelData[];
  tires: TireData[];
}

interface WheelCodeSearchTableProps {
  onSearchWheel?: (
    productCode: string,
  ) => Promise<Omit<WheelData, "isSearchMode"> & { mismatch?: boolean }>;
  onSearchTire?: (
    productCode: string,
  ) => Promise<Omit<TireData, "isSearchMode"> & { mismatch?: boolean }>;
  onRemoveWheel?: (productCode: string) => void;
  onRemoveTire?: (productCode: string) => void;
  wheelRowCount?: number;
  tireRowCount?: number;
}

// Helper functions
const getDisplayOrder = (wheelRowCount: number, tireRowCount: number) =>
  wheelRowCount < tireRowCount
    ? { first: "wheel" as const, second: "tire" as const }
    : { first: "tire" as const, second: "wheel" as const };

const getItemBoxStyle = (index: number, totalLength: number) => ({
  mb: index < totalLength - 1 ? 1 : 0,
  height: 50,
  display: "flex",
  alignItems: "center",
});

const getRemoveItemBoxStyle = (index: number, totalLength: number) => ({
  mb: index < totalLength - 1 ? 1 : 0,
  height: 50,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const createEmptyItem = (): BaseItemData => ({
  productCode: "",
  productName: "",
  size: "",
  loadIndex: "",
  isSearchMode: true,
  hasError: false,
  rimDiam: null,
});

// Components
const EditableTextField = memo<{
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  error?: boolean;
}>(({ value, onChange, error = false }) => (
  <TextField
    variant="outlined"
    value={value}
    onChange={onChange}
    InputProps={{ sx: styles.inputText }}
    error={error}
  />
));

const ReadOnlyTextField = memo<{
  value: string;
  leftAlign?: boolean;
  withBorder?: boolean;
  hasError?: boolean;
}>(({ value, leftAlign = false, withBorder = false, hasError = false }) => {
  const baseStyle = withBorder
    ? styles.inputText
    : leftAlign
      ? styles.inputTextLeftAlign
      : styles.inputTextNoBorder;

  const errorStyle = hasError
    ? {
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: "var(--red-800) !important",
          borderWidth: "2px",
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: "var(--red-800) !important",
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: "var(--red-800) !important",
        },
      }
    : {};

  return (
    <TextField
      value={value}
      variant="outlined"
      InputProps={{
        readOnly: true,
        sx: {
          ...baseStyle,
          ...errorStyle,
        },
      }}
    />
  );
});

const RemoveButton = memo<{
  onClick: () => void;
  ariaLabel?: string;
}>(({ onClick, ariaLabel = "削除" }) => (
  <IconButton onClick={onClick} aria-label={ariaLabel}>
    <RemoveCircleIcon sx={styles.removeButton} />
  </IconButton>
));

// Section Components
const ProductCodeSection = memo<{
  items: BaseItemData[];
  type: "wheel" | "tire";
  rowId: string;
  onInputChange: (type: TInputCode, value: string, itemIndex: number) => void;
}>(({ items, type, rowId, onInputChange }) => (
  <>
    <Typography sx={styles.sectionTitle}>
      {type === "wheel" ? "ホイール商品コード" : "タイヤ商品コード"}
    </Typography>
    {items.map((item, index) => (
      // eslint-disable-next-line react/no-array-index-key
      <Box key={`${type}-${rowId}-${index}`} sx={getItemBoxStyle(index, items.length)}>
        {item.isSearchMode ? (
          <EditableTextField
            value={item.productCode}
            onChange={(e) => onInputChange(type, e.target.value, index)}
            error={!!item.hasError}
          />
        ) : (
          <ReadOnlyTextField value={item.productCode} withBorder hasError={!!item.hasError} />
        )}
      </Box>
    ))}
  </>
));

const ProductNameSection = memo<{
  items: BaseItemData[];
  type: "wheel" | "tire";
  rowId: string;
  onSearch: (type: TInputCode, productCode: string, itemIndex: number) => void;
}>(({ items, type, rowId, onSearch }) => (
  <>
    <Typography sx={styles.sectionTitle}>&nbsp;</Typography>
    {items.map((item, index) => (
      // eslint-disable-next-line react/no-array-index-key
      <Box key={`${type}-name-${rowId}-${index}`} sx={getItemBoxStyle(index, items.length)}>
        {item.isSearchMode ? (
          <ButtonComponent
            onClick={() => onSearch(type, item.productCode, index)}
            theme="dark"
            sx={styles.searchButton}
          >
            検索
          </ButtonComponent>
        ) : (
          <ReadOnlyTextField value={item.productName} leftAlign />
        )}
      </Box>
    ))}
  </>
));

const SizeSection = memo<{
  items: BaseItemData[];
  type: "wheel" | "tire";
  rowId: string;
}>(({ items, type, rowId }) => (
  <>
    <Typography sx={styles.sectionTitle}>&nbsp;</Typography>
    {items.map((item, index) => (
      // eslint-disable-next-line react/no-array-index-key
      <Box key={`${type}-size-${rowId}-${index}`} sx={getItemBoxStyle(index, items.length)}>
        {item.isSearchMode ? <span>&nbsp;</span> : <ReadOnlyTextField value={item.size} />}
      </Box>
    ))}
  </>
));

const LoadIndexSection = memo<{
  items: BaseItemData[];
  type: "wheel" | "tire";
  rowId: string;
}>(({ items, type, rowId }) => (
  <>
    <Typography sx={styles.sectionTitle}>&nbsp;</Typography>
    {items.map((item, index) => (
      // eslint-disable-next-line react/no-array-index-key
      <Box key={`${type}-load-${rowId}-${index}`} sx={getItemBoxStyle(index, items.length)}>
        {item.isSearchMode ? <span>&nbsp;</span> : <ReadOnlyTextField value={item.loadIndex} />}
      </Box>
    ))}
  </>
));

const RemoveSection = memo<{
  items: BaseItemData[];
  type: "wheel" | "tire";
  rowId: string;
  onRemove: (type: TInputCode, itemIndex: number) => void;
}>(({ items, type, rowId, onRemove }) => (
  <>
    <Typography sx={styles.sectionTitle}>&nbsp;</Typography>
    {items.map((item, index) => (
      // eslint-disable-next-line react/no-array-index-key
      <Box key={`${type}-remove-${rowId}-${index}`} sx={getRemoveItemBoxStyle(index, items.length)}>
        {item.isSearchMode ? (
          <span>&nbsp;</span>
        ) : (
          <RemoveButton onClick={() => onRemove(type, index)} />
        )}
      </Box>
    ))}
  </>
));

// Search Row Component
const SearchTableRow = memo<{
  rowData: SearchRowData;
  rowIndex: number;
  wheelRowCount: number;
  tireRowCount: number;
  onSearch: (id: string, type: TInputCode, productCode: string, itemIndex?: number) => void;
  onRemove: (id: string, type: TInputCode, itemIndex?: number) => void;
  onInputChange: (
    id: string,
    type: TInputCode,
    value: string,
    itemIndex?: number,
    hasError?: boolean,
  ) => void;
}>(({ rowData, rowIndex, wheelRowCount, tireRowCount, onSearch, onRemove, onInputChange }) => {
  const displayOrder = getDisplayOrder(wheelRowCount, tireRowCount);

  const handleInputChange = useCallback(
    (type: TInputCode, value: string, itemIndex: number) => {
      // Only allow numeric input for product code
      if (!/^\d*$/.test(value)) return;
      // Clear error when user starts typing
      onInputChange(rowData.id, type, value, itemIndex, false);
    },
    [onInputChange, rowData.id],
  );

  const handleSearch = useCallback(
    (type: TInputCode, productCode: string, itemIndex: number) => {
      if (!productCode) {
        // Show error based on count comparison:
        // - wheelRowCount < tireRowCount: show error for wheel (ホイール商品コード)
        // - wheelRowCount > tireRowCount: show error for tire (タイヤ商品コード)
        const shouldShowWheelError = wheelRowCount < tireRowCount && type === "wheel";
        const shouldShowTireError = wheelRowCount > tireRowCount && type === "tire";

        if (shouldShowWheelError || shouldShowTireError) {
          // Set error state for the specific item
          onInputChange(rowData.id, type, productCode, itemIndex, true); // Add hasError flag
        }

        Notify.setNotification({
          show: true,
          title: "商品が選択されていません",
        });
        return;
      }
      onSearch(rowData.id, type, productCode, itemIndex);
    },
    [onSearch, rowData.id, wheelRowCount, tireRowCount, onInputChange],
  );

  const handleRemove = useCallback(
    (type: TInputCode, itemIndex: number) => {
      onRemove(rowData.id, type, itemIndex);
    },
    [onRemove, rowData.id],
  );

  const renderColumnContent = useCallback(
    (columnType: "code" | "name" | "size" | "load" | "remove") => {
      const firstSection = displayOrder.first;
      const secondSection = displayOrder.second;

      const firstItems = firstSection === "wheel" ? rowData.wheels : rowData.tires;
      const secondItems = secondSection === "wheel" ? rowData.wheels : rowData.tires;

      const showFirst = firstSection === "wheel" ? rowIndex === 0 : true;
      const showSecond = secondSection === "wheel" ? rowIndex === 0 : true;

      const renderSection = (items: BaseItemData[], type: "wheel" | "tire") => {
        const commonProps = { items, type, rowId: rowData.id };

        switch (columnType) {
          case "code":
            return <ProductCodeSection {...commonProps} onInputChange={handleInputChange} />;
          case "name":
            return <ProductNameSection {...commonProps} onSearch={handleSearch} />;
          case "size":
            return <SizeSection {...commonProps} />;
          case "load":
            return <LoadIndexSection {...commonProps} />;
          case "remove":
            return <RemoveSection {...commonProps} onRemove={handleRemove} />;
          default:
            return null;
        }
      };

      return (
        <>
          {showFirst && renderSection(firstItems, firstSection)}
          {showSecond && renderSection(secondItems, secondSection)}
        </>
      );
    },
    [displayOrder, rowData, rowIndex, handleInputChange, handleSearch, handleRemove],
  );

  return (
    <TableRow>
      <TableCellComponent sx={styles.tableCell}>
        <Box sx={styles.tableCellBox}>{renderColumnContent("code")}</Box>
      </TableCellComponent>

      <TableCellComponent sx={{ ...styles.tableCell, textAlign: "left" }}>
        <Box sx={styles.tableCellBoxLeft}>{renderColumnContent("name")}</Box>
      </TableCellComponent>

      <TableCellComponent sx={styles.tableCell}>
        <Box sx={styles.tableCellBox}>{renderColumnContent("size")}</Box>
      </TableCellComponent>

      <TableCellComponent sx={styles.tableCell}>
        <Box sx={styles.tableCellBox}>{renderColumnContent("load")}</Box>
      </TableCellComponent>

      <TableCellComponent sx={{ ...styles.tableCell, px: "2px" }}>
        <Box sx={styles.tableCellBox}>{renderColumnContent("remove")}</Box>
      </TableCellComponent>
    </TableRow>
  );
});

// Main Component
const WheelCodeSearchTable = memo<WheelCodeSearchTableProps>(
  ({
    onSearchWheel,
    onSearchTire,
    onRemoveWheel,
    onRemoveTire,
    wheelRowCount = 1,
    tireRowCount = 5,
  }) => {
    // Generate initial empty search rows
    const generateEmptyRow = useCallback(
      (index: number): SearchRowData => ({
        id: `row_${Date.now()}_${index}`,
        wheels: Array.from({ length: wheelRowCount }, createEmptyItem),
        tires: Array.from({ length: tireRowCount }, createEmptyItem),
      }),
      [wheelRowCount, tireRowCount],
    );

    const [searchRows, setSearchRows] = useState<SearchRowData[]>(() =>
      Array.from({ length: 1 }, (_, index) => generateEmptyRow(index)),
    );

    // Helper function to check which items have rim diameter mismatch
    const checkItemMismatches = useCallback((wheels: BaseItemData[], tires: BaseItemData[]) => {
      const mismatchedWheels = new Set<number>();
      const mismatchedTires = new Set<number>();

      // Get valid wheel and tire rim diameters
      const wheelRims = wheels
        .map((wheel, index) => ({
          index,
          rimDiam: !wheel.isSearchMode && wheel.rimDiam ? Number(wheel.rimDiam) : null,
        }))
        .filter((item) => item.rimDiam !== null && !Number.isNaN(item.rimDiam));

      const tireRims = tires
        .map((tire, index) => ({
          index,
          rimDiam: !tire.isSearchMode && tire.rimDiam ? Number(tire.rimDiam) : null,
        }))
        .filter((item) => item.rimDiam !== null && !Number.isNaN(item.rimDiam));

      // For each wheel, check if its rim diameter differs from any tire
      wheelRims.forEach((wheel) => {
        const hasMatchingTire = tireRims.some((tire) => wheel.rimDiam === tire.rimDiam);
        if (!hasMatchingTire && tireRims.length > 0) {
          mismatchedWheels.add(wheel.index);
        }
      });

      // For each tire, check if its rim diameter differs from any wheel
      tireRims.forEach((tire) => {
        const hasMatchingWheel = wheelRims.some((wheel) => tire.rimDiam === wheel.rimDiam);
        if (!hasMatchingWheel && wheelRims.length > 0) {
          mismatchedTires.add(tire.index);
        }
      });

      return { mismatchedWheels, mismatchedTires };
    }, []);

    // Handle search for wheel or tire
    const handleSearch = useCallback(
      async (id: string, type: TInputCode, productCode: string, itemIndex?: number) => {
        if (itemIndex === undefined) return;

        try {
          if (type === "wheel") {
            if (!onSearchWheel) return;
            const result = await onSearchWheel(productCode);

            setSearchRows((prev) =>
              prev.map((row) => {
                if (row.id !== id) return row;
                const newWheels = [...row.wheels];
                const newTires = [...row.tires];

                // Update the searched wheel item
                newWheels[itemIndex] = { ...result, isSearchMode: false, hasError: false };

                // First, clear all error states
                newWheels.forEach((wheel, index) => {
                  if (!wheel.isSearchMode) {
                    newWheels[index] = { ...wheel, hasError: false };
                  }
                });
                newTires.forEach((tire, index) => {
                  if (!tire.isSearchMode) {
                    newTires[index] = { ...tire, hasError: false };
                  }
                });

                // Check for rim diameter mismatches
                const { mismatchedWheels, mismatchedTires } = checkItemMismatches(
                  newWheels,
                  newTires,
                );

                // Apply errors based on wheelRowCount vs tireRowCount and specific mismatched items
                if (wheelRowCount < tireRowCount) {
                  // Show error on specific タイヤ商品コード fields that have mismatch
                  if (mismatchedTires.size > 0) {
                    const mismatchedTireCodes = Array.from(mismatchedTires)
                      .map((index) => newTires[index]?.productCode)
                      .filter(Boolean);
                    const tireCodesText = mismatchedTireCodes.join("、");
                    Notify.setNotification({
                      show: true,
                      title: `タイヤサイズ ${tireCodesText} がホイールサイズと異なります。`,
                    });
                  }
                  mismatchedTires.forEach((tireIndex) => {
                    if (
                      newTires[tireIndex] &&
                      newTires[tireIndex].productCode &&
                      !newTires[tireIndex].isSearchMode
                    ) {
                      newTires[tireIndex] = { ...newTires[tireIndex], hasError: true };
                    }
                  });
                } else if (wheelRowCount > tireRowCount) {
                  // Show error on specific ホイール商品コード fields that have mismatch
                  if (mismatchedWheels.size > 0) {
                    const mismatchedWheelCodes = Array.from(mismatchedWheels)
                      .map((index) => newWheels[index]?.productCode)
                      .filter(Boolean);
                    const wheelCodesText = mismatchedWheelCodes.join("、");
                    Notify.setNotification({
                      show: true,
                      title: `ホイールサイズ${wheelCodesText}はタイヤサイズと異なります。`,
                    });
                  }
                  mismatchedWheels.forEach((wheelIndex) => {
                    if (
                      newWheels[wheelIndex] &&
                      newWheels[wheelIndex].productCode &&
                      !newWheels[wheelIndex].isSearchMode
                    ) {
                      newWheels[wheelIndex] = { ...newWheels[wheelIndex], hasError: true };
                    }
                  });
                }

                // Handle mismatch flag from search result (for immediate feedback)
                if (result.mismatch) {
                  if (wheelRowCount >= tireRowCount) {
                    newWheels[itemIndex] = { ...newWheels[itemIndex], hasError: true };
                  } else {
                    mismatchedTires.forEach((tireIndex) => {
                      if (
                        newTires[tireIndex] &&
                        newTires[tireIndex].productCode &&
                        !newTires[tireIndex].isSearchMode
                      ) {
                        newTires[tireIndex] = { ...newTires[tireIndex], hasError: true };
                      }
                    });
                  }
                }

                return { ...row, wheels: newWheels, tires: newTires };
              }),
            );
          } else if (type === "tire") {
            if (!onSearchTire) return;
            const result = await onSearchTire(productCode);

            setSearchRows((prev) =>
              prev.map((row) => {
                if (row.id !== id) return row;
                const newWheels = [...row.wheels];
                const newTires = [...row.tires];

                // Update the searched tire item
                newTires[itemIndex] = { ...result, isSearchMode: false, hasError: false };

                // First, clear all error states
                newWheels.forEach((wheel, index) => {
                  if (!wheel.isSearchMode) {
                    newWheels[index] = { ...wheel, hasError: false };
                  }
                });
                newTires.forEach((tire, index) => {
                  if (!tire.isSearchMode) {
                    newTires[index] = { ...tire, hasError: false };
                  }
                });

                // Check for rim diameter mismatches
                const { mismatchedWheels, mismatchedTires } = checkItemMismatches(
                  newWheels,
                  newTires,
                );

                // Apply errors based on wheelRowCount vs tireRowCount and specific mismatched items
                if (wheelRowCount < tireRowCount) {
                  // Show error on specific タイヤ商品コード fields that have mismatch
                  if (mismatchedTires.size > 0) {
                    const mismatchedTireCodes = Array.from(mismatchedTires)
                      .map((index) => newTires[index]?.productCode)
                      .filter(Boolean);
                    const tireCodesText = mismatchedTireCodes.join("、");
                    Notify.setNotification({
                      show: true,
                      title: `タイヤサイズ ${tireCodesText} がホイールサイズと異なります。`,
                    });
                  }
                  mismatchedTires.forEach((tireIndex) => {
                    if (
                      newTires[tireIndex] &&
                      newTires[tireIndex].productCode &&
                      !newTires[tireIndex].isSearchMode
                    ) {
                      newTires[tireIndex] = { ...newTires[tireIndex], hasError: true };
                    }
                  });
                } else if (wheelRowCount > tireRowCount) {
                  // Show error on specific ホイール商品コード fields that have mismatch
                  if (mismatchedWheels.size > 0) {
                    const mismatchedWheelCodes = Array.from(mismatchedWheels)
                      .map((index) => newWheels[index]?.productCode)
                      .filter(Boolean);
                    const wheelCodesText = mismatchedWheelCodes.join("、");
                    Notify.setNotification({
                      show: true,
                      title: `ホイールサイズ${wheelCodesText}はタイヤサイズと異なります。`,
                    });
                  }
                  mismatchedWheels.forEach((wheelIndex) => {
                    if (
                      newWheels[wheelIndex] &&
                      newWheels[wheelIndex].productCode &&
                      !newWheels[wheelIndex].isSearchMode
                    ) {
                      newWheels[wheelIndex] = { ...newWheels[wheelIndex], hasError: true };
                    }
                  });
                }

                // Handle mismatch flag from search result (for immediate feedback)
                if (result.mismatch) {
                  if (wheelRowCount <= tireRowCount) {
                    newTires[itemIndex] = { ...newTires[itemIndex], hasError: true };
                  } else {
                    mismatchedWheels.forEach((wheelIndex) => {
                      if (
                        newWheels[wheelIndex] &&
                        newWheels[wheelIndex].productCode &&
                        !newWheels[wheelIndex].isSearchMode
                      ) {
                        newWheels[wheelIndex] = { ...newWheels[wheelIndex], hasError: true };
                      }
                    });
                  }
                }

                return { ...row, wheels: newWheels, tires: newTires };
              }),
            );
          }
        } catch (error) {
          // Keep in search mode on error silently
        }
      },
      [onSearchWheel, onSearchTire, wheelRowCount, tireRowCount, checkItemMismatches],
    );

    // Handle remove - convert back to search mode and call remove functions
    const handleRemove = useCallback(
      (id: string, type: TInputCode, itemIndex?: number) => {
        // Get the productCode from the item being removed
        const row = searchRows.find((r) => r.id === id);
        if (!row || itemIndex === undefined) return;

        const productCode =
          type === "wheel" ? row.wheels[itemIndex]?.productCode : row.tires[itemIndex]?.productCode;

        if (!productCode) return;

        // Call the remove function from props to update the hook state
        if (type === "wheel" && onRemoveWheel) {
          onRemoveWheel(productCode);
        }
        if (type === "tire" && onRemoveTire) {
          onRemoveTire(productCode);
        }

        // Update local state to convert back to search mode and clear errors
        setSearchRows((prev) =>
          prev.map((currentRow) => {
            if (currentRow.id !== id || itemIndex === undefined) return currentRow;

            if (type === "wheel") {
              const newWheels = [...currentRow.wheels];
              const newTires = [...currentRow.tires];
              newWheels[itemIndex] = createEmptyItem();

              return { ...currentRow, wheels: newWheels, tires: newTires };
            }

            if (type === "tire") {
              const newWheels = [...currentRow.wheels];
              const newTires = [...currentRow.tires];
              newTires[itemIndex] = createEmptyItem();

              return { ...currentRow, wheels: newWheels, tires: newTires };
            }

            return currentRow;
          }),
        );
      },
      [onRemoveWheel, onRemoveTire, searchRows],
    );

    // Handle input change
    const handleInputChange = useCallback(
      (id: string, type: TInputCode, value: string, itemIndex?: number, hasError?: boolean) => {
        setSearchRows((prev) =>
          prev.map((row) => {
            if (row.id !== id || itemIndex === undefined) return row;

            if (type === "wheel") {
              const newWheels = [...row.wheels];
              newWheels[itemIndex] = {
                ...newWheels[itemIndex],
                productCode: value,
                hasError: hasError !== undefined ? hasError : false,
              };
              return { ...row, wheels: newWheels };
            }

            if (type === "tire") {
              const newTires = [...row.tires];
              newTires[itemIndex] = {
                ...newTires[itemIndex],
                productCode: value,
                hasError: hasError !== undefined ? hasError : false,
              };
              return { ...row, tires: newTires };
            }

            return row;
          }),
        );
      },
      [],
    );

    return (
      <TableContainer component={Table} sx={styles.tableContainer}>
        <TableHead>
          <TableRowComponent data={WHEEL_CODE_HEADER} type="header" />
        </TableHead>
        <TableBody>
          {searchRows.map((row, rowIndex) => (
            <SearchTableRow
              key={row.id}
              rowData={row}
              rowIndex={rowIndex}
              wheelRowCount={wheelRowCount}
              tireRowCount={tireRowCount}
              onSearch={handleSearch}
              onRemove={handleRemove}
              onInputChange={handleInputChange}
            />
          ))}
        </TableBody>
      </TableContainer>
    );
  },
);

WheelCodeSearchTable.displayName = "WheelCodeSearchTable";

export default WheelCodeSearchTable;
